<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import { useMainStore } from "../store";
import type { Lead, QuizData, QuestionAnswer, UrlLogic, UrlVariant } from '../types';
import sha256 from "crypto-js/sha256";
import { trackQuizGateStart, trackQuizGateInput, trackQuizSubmit, trackQuizGateSkip } from '../analytics/events';
import { hashEmail } from '../analytics/hash';
import { getSessionId } from '../analytics/session';
import { buildRedirectUrl, skipUtmParams } from './redirect';


const store = useMainStore();
const quizData = store.quizData as QuizData;
const gate = store.gateData || {};
const lead: Lead = {} as Lead;
const form = ref(store.formLead);

const gateInputFired = ref(false);

watch(
  [() => form.value.name, () => form.value.email],
  ([name, email]) => {
    if (gateInputFired.value) return;
    if (name?.trim() && email?.trim()) {
      trackQuizGateInput({ quiz_id: quizData.quiz_id });
      gateInputFired.value = true;
    }
  }
);

onMounted(() => {
  trackQuizGateStart({
    quiz_id: quizData.quiz_id,
    questions_answered: store.questionAnswers.length,
  });
});


const setLead = () => {
  lead.quiz_id = quizData.quiz_id || '';
  lead.site_id = quizData.site_id || '';

  let direct_to: string | undefined = undefined;
  const leadAnswers = store.getLeadAnswers as unknown as QuestionAnswer[];
  const urlLogic: UrlLogic[] = store.quizUrlLogic || [];

  // Sort URLs by priority (lower number = higher priority)
  urlLogic.sort((a, b) => {
    if (a.priority === undefined && b.priority === undefined) return 0;
    if (a.priority === undefined) return 1;
    if (b.priority === undefined) return -1;
    return a.priority - b.priority;
  });

  // Find the first matching URL logic
  for (const url_logic of urlLogic) {
    const leadAnswerValues = leadAnswers.map((answer: QuestionAnswer) => answer.answer);
    const urlLogicAnswerValues = url_logic.answers.map(answer => answer.value);

    if (!url_logic.answers || url_logic.answers.length === 0 || leadAnswerValues.every((answer) => urlLogicAnswerValues.includes(answer))) {
      direct_to = url_logic.url;

      // Handle legacy url_variant with weight
      if (url_logic.url_variant && url_logic.variant_weight && Number(url_logic.variant_weight) > 0 && Number(url_logic.variant_weight) < 100) {
        if (Math.random() >= (1 - (parseInt(url_logic.variant_weight.toString()) / 100))) {
          direct_to = url_logic.url_variant;
        }
      // Handle url_variants array
      } else if (url_logic.url_variants) {
        // Handle legacy string array variants
        if (Array.isArray(url_logic.url_variants) && url_logic.url_variants.length > 0 && typeof url_logic.url_variants[0] === 'string') {
          const stringVariants = url_logic.url_variants as string[];
          if (stringVariants.length > 0) {
            const index = Math.floor(Math.random() * stringVariants.length);
            direct_to = stringVariants[index];
            break;
          }
        }

        // Handle new structure with weights
        const weightedVariants = (url_logic.url_variants as UrlVariant[]).filter((v): v is UrlVariant => typeof v === 'object' && v !== null && 'weight' in v && 'url' in v);
        const totalVariantWeight = weightedVariants.reduce((sum, variant) => sum + variant.weight, 0);
        const totalWeight = 100;

        // Check if weights exceed 100%
        if (totalVariantWeight > totalWeight) {
          console.error('Variant weights exceed 100%. Using default URL.');
          direct_to = url_logic.url;
          break;
        }

        // Handle empty variants array
        if (weightedVariants.length === 0) {
          direct_to = url_logic.url;
          break;
        }

        // Calculate cumulative weights
        let cumulativeWeight = 0;
        const variantWeights: number[] = [];
        weightedVariants.forEach(variant => {
          cumulativeWeight += variant.weight / totalWeight;
          variantWeights.push(cumulativeWeight);
        });

        // Add 100% weight for main URL, if no variant is found, defaults back to main url
        variantWeights.push(1);

        // Select URL based on random number
        const randomNum = Math.random();
        for (let i = 0; i < variantWeights.length; i++) {
          if (randomNum < variantWeights[i]) {
            direct_to = i < weightedVariants.length
              ? weightedVariants[i].url
              : url_logic.url;
            break;
          }
        }
      }

      if (direct_to) {
        break;
      }
    }
  }

  lead.direct_to = direct_to || quizData.quiz_default_url;
  lead.referrer = document.referrer;
  lead.origin = window.location.origin + window.location.pathname;

  // Cookies
  lead.fbp = store._getCookie('_fbp');
  lead.gid = store._getCookie('_gid');  // `gid` agora está definido em ExtendedLead

  // Query Parameters
  const queryParams = new URLSearchParams(window.location.search);
  lead.utm_source = queryParams.get('utm_source') || undefined;
  lead.utm_campaign = queryParams.get('utm_campaign') || undefined;
  lead.utm_medium = queryParams.get('utm_medium') || undefined;
  lead.utm_content = queryParams.get('utm_content') || undefined;
  lead.utm_term = queryParams.get('utm_term') || undefined;

  lead.fbclid = queryParams.get('fbclid') || undefined;
  // gclid is sourced from the mount-time snapshot (store.sessionAttributes.gclid),
  // not from queryParams here at submit. Locks attribution to the original landing
  // URL even if a future router guard mutates location.search mid-quiz. Issue #43.
  lead.gclid = store.sessionAttributes.gclid || undefined;
  lead.sub_id = queryParams.get('sub_id') || undefined;

  // Ride the mount-captured ad-attribution block out with the rest of the lead.
  // Always present (Pinia state defaults to all-empty), so the API contract is
  // stable whether or not the user arrived via a paid ad. Issue #43.
  lead.session_attributes = store.sessionAttributes;

  // Enrich answers with friendly texts
  lead.question_answers = leadAnswers.map(answer => {
    // Search for the corresponding question
    const question = quizData.questions?.find(q => q.id === answer.id);

    // Search for the answer text
    let answerText = answer.answer; // fallback for the original value

    if (question && question.answers) {
      // Check if it's an array (current format of quiz.json)
      const answerData = question.answers.find((ans) => {
        return Array.isArray(ans) && ans[0] === answer.answer;
      }) as string[] | undefined;

      if (answerData && Array.isArray(answerData)) {
        answerText = answerData[1] || answer.answer; // Use raw text without slug conversion
      }
    }

    return {
      id: question ? question.title : answer.id, // Use question title in raw format
      answer: answerText  // Now using friendly text in raw format
    } as QuestionAnswer;
  });
};

const handleSubmit = async () => {
  setLead();
  lead.name = form.value.name;
  lead.email = form.value.email;
  const normalizedEmail = (form.value.email || '').trim().toLowerCase();
  const normalizedName = (form.value.name || '').trim().toLowerCase();
  lead.external_id = sha256(normalizedEmail).toString();
  lead.hashed_email = sha256(normalizedEmail).toString();
  lead.hashed_name = sha256(normalizedName).toString();
  lead.phone = form.value.phone;

  // Read once, reuse for both API POST and redirect hash so leadclump-flow's
  // landing-side lead_check correlates to the same row leadclump-quiz-api stores.
  const session_id = getSessionId();
  lead.session_id = session_id;

  await store.createLead(lead);

  const additionalParams = {
    utm_source: lead.utm_source,
    utm_medium: lead.utm_medium,
    utm_campaign: lead.utm_campaign,
    utm_content: lead.utm_content,
    utm_term: lead.utm_term,
    fbclid: lead.fbclid,
    gclid: lead.gclid,
    sub_id: lead.sub_id,
  } as Record<string, string>;

  // l_e is leadclump-flow's email-verification anchor. l_s is the session correlation
  // ID. Both are unconditional: the prior `if (!store.isLeadValidAndSafe)` gate was
  // dead code (the in-quiz lead check it referenced was removed for speed; landing
  // verification by leadclump-flow is the source of truth now).
  const hashParams: Record<string, string> = {
    l_e: lead.hashed_email,
    l_s: session_id,
  };

  const redirect_url = buildRedirectUrl(lead.direct_to || '', additionalParams, hashParams);

  // Defense in depth: the form's `required` attribute should already prevent
  // empty submission, but if it ever reaches handleSubmit (programmatic submit,
  // future template refactor that drops `required`, etc.) hashEmail('') would
  // return the sha256 of the empty string — a real-looking 64-char hex value.
  // Shipping that to GAds as a user_data anchor would pollute attribution with
  // a stable "non-user" identifier shared across every empty submission.
  const hasEmail = !!(form.value.email && form.value.email.trim());
  const emailHash = hasEmail ? hashEmail(form.value.email) : '';

  trackQuizSubmit({
    quiz_id: quizData.quiz_id,
    email_sha256: emailHash,
    redirect_url,
  });

  // Persist normalized hash so GTM client can attach it to ad-view events
  // (adViewable/rewardViewable) fired by the wrapper on the landing page.
  // Only write when we actually have an email — see comment above.
  if (hasEmail) {
    store._setCookie('leadclump_e_sha256', emailHash, 30);
  }

  // Persist gclid for Web GTM → conversions-api attribution (DEV-787)
  // Web GTM reads pblk_gclid to fire rewarded-ad-view conversions against the
  // originating Google Ads click. 90-day TTL aligns with the default
  // click-through attribution window (vs 30-day email hash cookie).
  // Guard: only write when gclid is present — first-time organic/direct traffic
  // gets no cookie. A stale pblk_gclid from a prior paid click WILL persist
  // (intentional: last-click attribution semantics — GAds matches conversions
  // to the most-recent paid click within 90 days, regardless of whether the
  // current session arrived from Google or organically).
  // lead.gclid is populated by setLead() above via URLSearchParams.get('gclid').
  if (lead.gclid) {
    store._setCookie('pblk_gclid', lead.gclid, 90);
  }

  store.resetQuiz();

  if (window.location !== window.parent.location) {
    window.top!.location.href = redirect_url;
  } else {
    window.location.href = redirect_url;
  }
};

const handleSkip = () => {
  setLead();

  const additionalParams = {
    utm_source: lead.utm_source,
    utm_medium: lead.utm_medium,
    utm_campaign: lead.utm_campaign,
    utm_content: lead.utm_content,
    utm_term: lead.utm_term,
    fbclid: lead.fbclid,
    gclid: lead.gclid,
    sub_id: lead.sub_id,
    // Spread last so the stamp overrides the inbound UTM it relabels — this is the
    // gate skip button, the only skip path live on the lead-gen ops. DEV-1418.
    ...skipUtmParams(quizData),
  } as Record<string, string>;

  // Skip path has no email, so no l_e — only the session correlation ID so the
  // funnel team can join abandoned-gate sessions back to the quiz session row.
  const hashParams: Record<string, string> = { l_s: getSessionId() };

  const redirect_url = buildRedirectUrl(lead.direct_to || '', additionalParams, hashParams);

  trackQuizGateSkip({
    quiz_id: quizData.quiz_id,
    questions_answered: store.questionAnswers.length,
    redirect_url,
  });

  store.resetQuiz();

  if (window.location !== window.parent.location) {
    window.top!.location.href = redirect_url;
  } else {
    window.location.href = redirect_url;
  }
};


</script>

<template>
  <div class="step step-5 step-form active">
    <h4>{{ gate.title || '' }}</h4>
    <h6 v-if="gate.sub_title">{{ gate.sub_title }}</h6>
    <div class="text-center" v-if="gate.image">
      <img :src="gate.image" :alt="gate.title" />
    </div>
    <p v-if="gate.description">{{ gate.description }}</p>
    <form @submit.prevent="handleSubmit" class="needs-validation novalidate">
      <input
        type="text"
        name="name"
        :placeholder="gate.namePlaceholder"
        :aria-label="gate.namePlaceholder"
        required
        v-model="form.name"
        :title="gate.nameError"
        minlength="3"
      >
      <input
        v-if="gate.usePhone"
        type="tel"
        name="phone"
        placeholder="Digite seu telefone"
        aria-label="Digite seu telefone"
        required
        v-model="form.phone"
      >
      <input
        v-if="gate.useEmail"
        type="email"
        name="email"
        :placeholder="gate.emailPlaceholder"
        :aria-label="gate.emailPlaceholder"
        required
        v-model="form.email"
        pattern="^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$"
        :title="gate.emailError"
      >
      <button type="submit" :disabled="store.fetching" class="cta-button">
        <span v-if="!store.fetching">{{ gate.cta }}</span>
        <span v-else class="loading-content">
          {{ gate.loading }} <font-awesome-icon icon="fas fa-spinner" class="loading-icon" spin />
        </span>
      </button>
      <button
        v-if="quizData.skip && quizData.skip_cta"
        type="button"
        @click="handleSkip"
        :disabled="store.fetching"
        class="skip-button"
      >
        {{ quizData.skip_cta }}
      </button>
    </form>
  </div>
</template>